/**
 * Dropdown component - Data-driven dropdown with multi-selection support
 * Uses Modal for positioning and DropdownItem for rendering
 * Factory function following the app's architectural patterns
 */

import {
  getModalManager,
  type ModalShowConfig,
} from "@UI/ModalManager/index.js";
import { type ModalAnchor } from "@UI/Modal/index.js";
import {
  createDropdownItem,
  type DropdownItemAPI,
} from "@UI/DropdownItem/index.js";

export interface DropdownItemData {
  readonly id: string;
  readonly icon?: string;
  readonly label: string;
  readonly selected?: boolean;
  readonly disabled?: boolean;
}

export interface DropdownConfig {
  readonly data: readonly DropdownItemData[];
  readonly multiSelect?: boolean;
  readonly className?: string;
  readonly maxHeight?: string;
  readonly onSelectionChange?: (selectedIds: readonly string[]) => void;
  readonly onClose?: () => void;

  // Modal positioning options
  readonly anchor?: ModalAnchor;
  readonly anchorElement?: HTMLElement;
  readonly offset?: { x: number; y: number };
}

export interface DropdownAPI {
  readonly getElement: () => HTMLElement;
  readonly show: () => void;
  readonly hide: () => void;
  readonly isVisible: () => boolean;
  readonly updateData: (data: readonly DropdownItemData[]) => void;
  readonly getSelectedIds: () => readonly string[];
  readonly setSelectedIds: (ids: readonly string[]) => void;
  readonly destroy: () => void;
}

/**
 * Creates Dropdown component with data-driven rendering and selection
 * @param config - Configuration for the dropdown
 * @returns Dropdown API
 */
export const createDropdown = (config: DropdownConfig): DropdownAPI => {
  const {
    data,
    multiSelect = false,
    className = "",
    maxHeight = "300px",
    onSelectionChange,
    onClose,
    anchor = "bottom-left",
    anchorElement,
    offset = { x: 0, y: 4 },
  } = config;

  let isDestroyed = false;
  let currentData = [...data];
  let selectedIds = new Set(
    data.filter((item) => item.selected).map((item) => item.id)
  );

  // Component references
  const modalManager = getModalManager();
  let itemAPIs: Map<string, DropdownItemAPI> = new Map();

  // Create dropdown container
  const container = document.createElement("div");
  container.className = `dropdown ${className}`.trim();
  container.style.maxHeight = maxHeight;

  /**
   * Create check control for multi-select items
   */
  const createCheckControl = (checked: boolean): HTMLElement => {
    const control = document.createElement("div");
    control.className = "dropdown-check";
    control.innerHTML = checked ? "✓" : "";
    return control;
  };

  /**
   * Handle item selection
   */
  const handleItemSelection = (itemId: string): void => {
    if (isDestroyed) return;

    const item = currentData.find((d) => d.id === itemId);
    if (!item || item.disabled) return;

    if (multiSelect) {
      // Toggle selection in multi-select mode
      if (selectedIds.has(itemId)) {
        selectedIds.delete(itemId);
      } else {
        selectedIds.add(itemId);
      }

      // Update the visual state of the item
      const itemAPI = itemAPIs.get(itemId);
      if (itemAPI) {
        const isSelected = selectedIds.has(itemId);
        itemAPI.setSelected(isSelected);
        itemAPI.setControlContent(createCheckControl(isSelected));
      }
    } else {
      // Single selection - replace current selection
      selectedIds.clear();
      selectedIds.add(itemId);

      // Update all items
      updateItemStates();

      // Close dropdown in single-select mode
      hide();
    }

    // Notify selection change
    onSelectionChange?.(Array.from(selectedIds));
  };

  /**
   * Update visual states of all items
   */
  const updateItemStates = (): void => {
    itemAPIs.forEach((itemAPI, itemId) => {
      const isSelected = selectedIds.has(itemId);
      itemAPI.setSelected(isSelected);

      if (multiSelect) {
        itemAPI.setControlContent(createCheckControl(isSelected));
      }
    });
  };

  /**
   * Render dropdown items from data
   */
  const renderItems = (): void => {
    // Clear existing items
    itemAPIs.forEach((itemAPI) => itemAPI.destroy());
    itemAPIs.clear();
    container.innerHTML = "";

    // Create new items
    currentData.forEach((itemData) => {
      const isSelected = selectedIds.has(itemData.id);

      const itemAPI = createDropdownItem({
        ...(itemData.icon && { icon: itemData.icon }),
        label: itemData.label,
        ...(itemData.disabled && { disabled: itemData.disabled }),
        selected: isSelected,
        showControl: multiSelect,
        ...(multiSelect && { controlContent: createCheckControl(isSelected) }),
        onClick: () => handleItemSelection(itemData.id),
      });

      itemAPIs.set(itemData.id, itemAPI);
      container.appendChild(itemAPI.getElement());
    });
  };

  /**
   * Build modal config for showing
   */
  const buildModalConfig = (): ModalShowConfig => {
    return {
      content: container,
      anchor,
      ...(anchorElement && { anchorElement }),
      offset,
      closeOnBackdrop: true,
      onClose: () => {
        onClose?.();
      },
    };
  };

  /**
   * Show dropdown
   */
  const show = (): void => {
    if (isDestroyed) return;

    const config = buildModalConfig();
    modalManager.show(config);
  };

  /**
   * Hide dropdown
   */
  const hide = (): void => {
    if (isDestroyed) return;

    // Only hide if this dropdown is currently shown
    if (modalManager.getCurrentContent() === container) {
      modalManager.hide();
    }
  };

  /**
   * Update dropdown data
   */
  const updateData = (newData: readonly DropdownItemData[]): void => {
    if (isDestroyed) return;

    currentData = [...newData];

    // Preserve existing selections that are still valid
    const validIds = new Set(newData.map((item) => item.id));
    selectedIds = new Set(
      Array.from(selectedIds).filter((id) => validIds.has(id))
    );

    // Add any new pre-selected items
    newData.forEach((item) => {
      if (item.selected) {
        selectedIds.add(item.id);
      }
    });

    renderItems();
  };

  /**
   * Get current selection
   */
  const getSelectedIds = (): readonly string[] => {
    return Array.from(selectedIds);
  };

  /**
   * Set selection
   */
  const setSelectedIds = (ids: readonly string[]): void => {
    if (isDestroyed) return;

    selectedIds = new Set(ids);
    updateItemStates();
    onSelectionChange?.(Array.from(selectedIds));
  };

  /**
   * Clean up resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Hide modal if this dropdown is currently shown
    if (modalManager.getCurrentContent() === container) {
      modalManager.hide();
    }

    // Destroy all item APIs
    itemAPIs.forEach((itemAPI) => itemAPI.destroy());
    itemAPIs.clear();

    // Clear data
    selectedIds.clear();
  };

  // Initialize
  renderItems();

  return {
    getElement: () => container,
    show,
    hide,
    isVisible: () => modalManager.getCurrentContent() === container,
    updateData,
    getSelectedIds,
    setSelectedIds,
    destroy,
  };
};

/**
 * Creates Dropdown component and appends to container
 * @param container - Container element to append to
 * @param config - Configuration for the dropdown
 * @returns Dropdown API
 */
export const createDropdownInContainer = (
  container: HTMLElement,
  config: DropdownConfig
): DropdownAPI => {
  const dropdown = createDropdown(config);
  container.appendChild(dropdown.getElement());
  return dropdown;
};
